'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { Eye, EyeOff, ShieldCheck, LogIn, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

type FormData = {
  email: string;
  password: string;
  remember: boolean;
};

export default function AdminLoginForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const { signIn } = useAuth();

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>();

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    try {
      await signIn(data.email, data.password);
      toast.success('Login successful — welcome back!');
      router.push('/admin-dashboard');
      router.refresh();
    } catch (error: any) {
      const message =
        error?.message === 'Invalid login credentials' ?'Invalid email or password. Please try again.' : error?.message ||'Login failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md">
      <div className="flex items-center gap-2 mb-8 lg:hidden">
        <AppLogo size={32} />
        <span className="text-xl font-bold text-foreground">Store<span className="text-primary">Shield</span></span>
      </div>

      <h1 className="text-2xl font-bold text-foreground mb-1">Admin Login</h1>
      <p className="text-sm text-muted-foreground mb-8">
        Sign in to the Make me Authentic verification dashboard
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-semibold text-foreground mb-1.5">
            Email Address
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="you@makemeauthentic.in"
            suppressHydrationWarning
            className={`w-full px-4 py-3 rounded-xl border text-sm bg-card text-foreground placeholder:text-muted-foreground outline-none transition-all duration-150 focus:ring-2 focus:ring-ring focus:border-primary
              ${errors.email ? 'border-destructive' : 'border-border'}`}
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
            })}
          />
          {errors.email && (
            <p className="text-destructive text-xs mt-1.5">{errors.email.message}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <label htmlFor="password" className="block text-sm font-semibold text-foreground mb-1.5">
            Password
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Enter your password"
              suppressHydrationWarning
              className={`w-full px-4 py-3 pr-12 rounded-xl border text-sm bg-card text-foreground placeholder:text-muted-foreground outline-none transition-all duration-150 focus:ring-2 focus:ring-ring focus:border-primary
                ${errors.password ? 'border-destructive' : 'border-border'}`}
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 6, message: 'Password must be at least 6 characters' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.password && (
            <p className="text-destructive text-xs mt-1.5">{errors.password.message}</p>
          )}
        </div>

        {/* Remember me */}
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 rounded border-border accent-primary"
              {...register('remember')}
            />
            <span className="text-sm text-muted-foreground">Remember me for 30 days</span>
          </label>
          <button type="button" className="text-sm text-primary font-medium hover:underline">
            Forgot password?
          </button>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 py-3.5 bg-primary text-primary-foreground rounded-xl font-semibold text-sm hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed active:scale-95 transition-all duration-150"
          style={{ minHeight: 48 }}
        >
          {loading ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <>
              <LogIn size={18} />
              Sign In to Dashboard
            </>
          )}
        </button>
      </form>

      {/* Info note */}
      <div className="mt-8 p-4 bg-muted rounded-2xl border border-border">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck size={15} className="text-primary" />
          <span className="text-xs font-semibold text-foreground uppercase tracking-wide">Secure Admin Access</span>
        </div>
        <p className="text-xs text-muted-foreground">
          This portal is restricted to authorized Make me Authentic administrators. Your session is secured with JWT tokens and managed by Supabase Auth.
        </p>
      </div>
    </div>
  );
}